import { JSX } from 'react';
import {
  HomefieldIconSocialFacebook,
  HomefieldIconSocialInstagram,
  HomefieldIconSocialPinterest,
  HomefieldIconSocialX,
  HomefieldIconSocialYoutube,
} from '@dsg-tech/homefield-react-icons';
import { SocialMediaLSites, envData } from 'environments';

interface BaseSocialIconLinkProps {
  site: SocialMediaLSites;
  link?: string;
  // The goal here is to pass through any additional props to the Homefield Icon components. This is
  // the generated type signature. Using this instead of deriving this from ComponentProps because
  // each Icon has its own type signature and that would be difficult to keep updated and scale
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const BaseSocialIconLink = ({ link = '', site, ...props }: BaseSocialIconLinkProps) => {
  let ariaLabel = '';
  let dataEm = '';
  let icon: JSX.Element | null = null;

  switch (site) {
    case 'facebook':
      ariaLabel = 'follow us on facebook';
      dataEm = 'Footer_Facebook';
      icon = <HomefieldIconSocialFacebook {...props} />;
      break;
    case 'twitter':
      ariaLabel = 'follow us on X';
      dataEm = 'Footer_Twitter';
      icon = <HomefieldIconSocialX {...props} />;
      break;
    case 'instagram':
      ariaLabel = 'follow us on instagram';
      dataEm = 'Footer_Instagram';
      icon = <HomefieldIconSocialInstagram {...props} />;
      break;
    case 'pinterest':
      ariaLabel = 'follow us on pinterest';
      dataEm = 'Footer_Pinterest';
      icon = <HomefieldIconSocialPinterest {...props} />;
      break;
    case 'youtube':
      ariaLabel = 'follow us on YouTube';
      dataEm = 'Footer_Youtube';
      icon = <HomefieldIconSocialYoutube {...props} />;
      break;
    // exit early if we don't have a case for handling this icon
    default:
      return null;
  }

  return (
    <a
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      href={link}
      data-em={dataEm}>
      {icon}
    </a>
  );
};

interface SocialIconLinkProps {
  site: string;
  brand: string;
  // The goal here is to pass through any additional props to the Homefield Icon components. This is
  // the generated type signature. Using this instead of deriving this from ComponentProps because
  // each Icon has its own type signature and that would be difficult to keep updated and scale
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [iconProp: string]: any;
}

const SocialIconLink = ({ site, brand, ...props }: SocialIconLinkProps) => {
  // exit early if we don't have any socialLinks specified for the given brand
  if (!(brand in envData)) {
    return null;
  }
  const { socialLinks } = envData[brand as keyof typeof envData];
  // exit early if the brand doesn't have a social media link for the given site
  if (!(site in socialLinks)) {
    return null;
  }
  const link = socialLinks[site as keyof typeof socialLinks];
  return <BaseSocialIconLink site={site as SocialMediaLSites} link={link} {...props} />;
};

export default SocialIconLink;
